import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Calendar, User, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const { t, i18n } = useTranslation();
  const isZh = i18n.language.startsWith("zh");

  useEffect(() => {
    api.get("/blog", { params: { published_only: true } }).then((r) => setPosts(r.data));
  }, []);

  const title = (p) => (isZh && p.title_zh) ? p.title_zh : p.title;
  const excerpt = (p) => (isZh && p.excerpt_zh) ? p.excerpt_zh : p.excerpt;

  return (
    <div className="pt-32 pb-20 min-h-screen" data-testid="blog-page">
      <div className="container-tulsi">
        <div className="text-center mb-14">
          <p className="text-[10px] tracking-[0.5em] uppercase text-[#A90011] mb-4 font-medium">— {t("blog.subtitle")} —</p>
          <h1 className="font-serif-display text-6xl md:text-8xl text-gold-gradient leading-[1.02]">{t("blog.title")}</h1>
          <div className="gold-separator" />
          <p className="font-serif italic text-lg md:text-xl text-[#2A0708] max-w-xl mx-auto mt-6">{t("blog.intro")}</p>
        </div>

        {posts.length === 0 ? (
          <p className="text-center text-[#6B4A4E] py-20" data-testid="no-posts-msg">{t("blog.noPosts")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {posts.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                whileHover={{ y: -6 }}
                className="glass-card overflow-hidden group flex flex-col"
                data-testid={`blog-card-${p.id}`}
              >
                <Link to={`/blog/${p.slug}`} className="block">
                  {p.cover_image ? (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={p.cover_image} alt={title(p)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]" />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] flex items-center justify-center" style={{ background: "linear-gradient(135deg, #FBEBD8, #F4DDC0)" }}>
                      <span className="text-6xl opacity-30 select-none">📖</span>
                    </div>
                  )}
                </Link>
                <div className="p-7 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 text-[10px] tracking-[0.2em] uppercase text-[#8E5F65] mb-3">
                    <span className="flex items-center gap-1"><Calendar size={11} /> {new Date(p.created_at).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1"><User size={11} /> {p.author}</span>
                  </div>
                  <Link to={`/blog/${p.slug}`}>
                    <h3 className="font-serif-display text-2xl text-[#2A0708] leading-tight hover:text-[#A90011] transition-colors">{title(p)}</h3>
                  </Link>
                  {excerpt(p) && <p className="text-sm text-[#6B4A4E] mt-3 leading-relaxed line-clamp-3 flex-1">{excerpt(p)}</p>}
                  <Link to={`/blog/${p.slug}`} className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#A90011] hover:text-[#D11D21] font-medium" data-testid={`read-more-${p.id}`}>
                    {t("blog.readMore")} <ArrowRight size={12} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function BlogDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { t, i18n } = useTranslation();
  const isZh = i18n.language.startsWith("zh");

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="pt-32 pb-20 min-h-screen container-tulsi text-center"><p className="text-[#6B4A4E]">Story not found.</p><Link to="/blog" className="text-[#A90011] mt-4 inline-block">{t("blog.backToBlog")}</Link></div>;
  if (!post) return <div className="pt-32 pb-20 min-h-screen container-tulsi text-center"><p className="text-[#6B4A4E]">{t("common.loading")}</p></div>;

  const title = (isZh && post.title_zh) ? post.title_zh : post.title;
  const content = (isZh && post.content_zh) ? post.content_zh : post.content;

  return (
    <article className="pt-32 pb-20 min-h-screen" data-testid="blog-detail-page">
      <div className="container-tulsi max-w-3xl">
        <Link to="/blog" className="text-xs tracking-[0.25em] uppercase text-[#6B4A4E] hover:text-[#A90011]" data-testid="back-to-blog">{t("blog.backToBlog")}</Link>

        <div className="text-center mt-8 mb-10">
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#A90011] mb-4">— {t("blog.subtitle")} —</p>
          <h1 className="font-serif-display text-4xl md:text-6xl text-[#2A0708] leading-[1.1]">{title}</h1>
          <div className="gold-separator" />
          <p className="text-xs tracking-[0.2em] uppercase text-[#8E5F65]">
            {t("blog.by")} {post.author} · {new Date(post.created_at).toLocaleDateString()}
          </p>
        </div>

        {post.cover_image && (
          <div className="frame-elegant rounded-2xl overflow-hidden mb-10">
            <img src={post.cover_image} alt={title} className="w-full h-auto" />
          </div>
        )}

        <div
          className="prose prose-stone max-w-none font-serif text-lg text-[#2A0708] leading-[1.85] whitespace-pre-wrap"
          data-testid="blog-content"
        >
          {content}
        </div>
      </div>
    </article>
  );
}
